/**
 * The Firebase client, and the deliberate decision not to let it keep a
 * session.
 *
 * **We must not run two session stores.** Ours is the JWT pair in
 * `store/authStore.ts`; a Firebase ID token is a single-use credential we
 * exchange once at `/auth/firebase` and then have no further use for. Leaving
 * Firebase on its default `browserLocalPersistence` would give the browser a
 * second, independently expiring session that outlives `clearSession()`, makes
 * `onAuthStateChanged` a competing source of truth about who is signed in, and
 * — worst of the three — leaves a refreshable Firebase session alive on a
 * shared machine after the user pressed Sign Out.
 *
 * **`browserSessionPersistence`, not `inMemoryPersistence`.** In-memory would
 * be the strictest choice, but `signInWithRedirect` — the fallback when a popup
 * is blocked, which is also the Safari path — needs the pending sign-in to
 * survive a full page navigation. sessionStorage survives that and still cannot
 * outlive the tab.
 *
 * That bounds the blast radius; it does not close it. `clearSession()` signs
 * out of Firebase explicitly as well, or a stale session resurrects in the next
 * tab.
 */

import { getApps, initializeApp } from 'firebase/app'
import {
    GoogleAuthProvider,
    OAuthProvider,
    browserSessionPersistence,
    createUserWithEmailAndPassword,
    getAuth,
    setPersistence,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    signOut,
    updateProfile,
} from 'firebase/auth'
import appConfig from '@/configs/app.config'
import type { Auth } from 'firebase/auth'
import type { FirebaseApp } from 'firebase/app'

const APP_NAME = 'trackheros'

let persistenceApplied: Promise<void> | null = null

/**
 * The initialised app, or null when no project is configured.
 *
 * `getApps()` is checked first because React 19 mounts components twice in
 * development, and `initializeApp` with a name that already exists throws.
 */
const getFirebaseApp = (): FirebaseApp | null => {
    if (!appConfig.firebaseEnabled) {
        return null
    }

    const existing = getApps().find((app) => app.name === APP_NAME)
    if (existing) {
        return existing
    }

    return initializeApp(appConfig.firebase, APP_NAME)
}

export const getFirebaseAuth = (): Auth | null => {
    const app = getFirebaseApp()
    if (!app) {
        return null
    }

    const auth = getAuth(app)

    // Applied once, and the promise is reused rather than awaited per call —
    // `setPersistence` resolves immediately after the first time, and a caller
    // that forgot to await it would get the default persistence for its own
    // sign-in only, which is the kind of bug that shows up on one browser.
    if (!persistenceApplied) {
        persistenceApplied = setPersistence(auth, browserSessionPersistence)
    }

    return auth
}

/** Throws rather than returning null, for the paths that cannot proceed without it. */
const requireAuth = (): Auth => {
    const auth = getFirebaseAuth()
    if (!auth) {
        throw new Error('Firebase is not configured in this build.')
    }
    return auth
}

const ready = async (): Promise<Auth> => {
    const auth = requireAuth()
    await persistenceApplied
    return auth
}

/**
 * Sign in, hand back the ID token, and **end the Firebase session immediately**.
 *
 * The token is the only thing we want. Keeping the Firebase user signed in
 * afterwards would be the second session store this module exists to avoid,
 * and we never need it again — our own JWT refresh carries the session from
 * here on, and `/auth/refresh` never touches Firebase.
 */
const tokenThenSignOut = async (
    auth: Auth,
    credential: { user: { getIdToken: () => Promise<string> } },
): Promise<string> => {
    const idToken = await credential.user.getIdToken()
    // Not awaited into the happy path: the exchange must not fail because the
    // tidy-up did. A leftover tab-scoped session is a much smaller problem than
    // a sign-in that reports failure after succeeding.
    void signOut(auth).catch(() => {})
    return idToken
}

export const firebaseSignInWithEmail = async (
    email: string,
    password: string,
): Promise<string> => {
    const auth = await ready()
    const credential = await signInWithEmailAndPassword(auth, email, password)
    return tokenThenSignOut(auth, credential)
}

export const firebaseSignUpWithEmail = async (
    email: string,
    password: string,
    displayName?: string,
): Promise<string> => {
    const auth = await ready()
    const credential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
    )

    if (displayName) {
        // Before the exchange, so the backend's first sight of this account
        // already has a name on it. The `userName` field in the exchange body
        // is the fallback for a client that skipped this.
        await updateProfile(credential.user, { displayName })
    }

    return tokenThenSignOut(auth, credential)
}

/**
 * A popup, falling back to a redirect.
 *
 * Popups are blocked by default in more places than people expect — Safari with
 * ITP, embedded browsers, and any browser where the click has been through a
 * handler that lost the user-gesture. `signInWithRedirect` always works and is
 * only worse because it leaves the page, which is why it is the fallback and
 * not the default.
 */
const popupOrRedirect = async (
    auth: Auth,
    provider: GoogleAuthProvider | OAuthProvider,
): Promise<string> => {
    try {
        const credential = await signInWithPopup(auth, provider)
        return tokenThenSignOut(auth, credential)
    } catch (error) {
        const code = (error as { code?: string })?.code
        if (
            code === 'auth/popup-blocked' ||
            code === 'auth/operation-not-supported-in-this-environment'
        ) {
            await signInWithRedirect(auth, provider)
            // The page is navigating away; nothing after this runs.
            return ''
        }
        throw error
    }
}

export const firebaseSignInWithGoogle = async (): Promise<string> => {
    const auth = await ready()
    const provider = new GoogleAuthProvider()
    // These users are signed into several Google accounts at once — one per
    // client, typically. Letting Google pick silently is the single most
    // predictable support ticket in this feature.
    provider.setCustomParameters({ prompt: 'select_account' })
    return popupOrRedirect(auth, provider)
}

export const firebaseSignInWithMicrosoft = async (): Promise<string> => {
    const auth = await ready()
    const provider = new OAuthProvider('microsoft.com')
    // Same reasoning, more strongly: a freelancer has a personal MSA plus a
    // guest account in each client's tenant, and a silent SSO into the wrong
    // one looks like the product losing their data.
    provider.setCustomParameters({ prompt: 'select_account' })
    return popupOrRedirect(auth, provider)
}

/**
 * End any Firebase session. Safe to call when Firebase is not configured.
 *
 * Called from `clearSession()` as well as after each exchange — belt and
 * braces, because the cost of a missed sign-out is a live session on somebody
 * else's machine.
 */
export const firebaseSignOut = async (): Promise<void> => {
    const auth = getFirebaseAuth()
    if (!auth) {
        return
    }
    await signOut(auth)
}
